/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx, css } from '@emotion/react'
import produce from 'immer'
import { IconButton, Paper, TableHead, TablePagination, TableRow, Tooltip } from '@mui/material'
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight'
import {
  usePagination,
  useSortBy,
  useTable,
  PluginHook,
  TableState,
  useRowSelect,
  ColumnInstance,
  Row,
  CellProps,
  useExpanded,
  ActionType,
  useGlobalFilter,
  useFilters,
  FilterTypes,
  Renderer,
  FilterProps,
  useBlockLayout,
  useResizeColumns,
  useFlexLayout,
  IdType,
} from 'react-table'
import IndeterminateCheckbox, { CHECKBOX_WIDTH } from '../DataTable/TablePieces/IndeterminateCheckbox.tsx'
import Toolbar from '../DataTable/TablePieces/Toolbar.tsx'
import { Fragment, PropsWithChildren, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import AppliedFilters from '../DataTable/TablePieces/AppliedFilters.tsx'
import { SimpleMap } from '@vendorflow/types'
import { values } from 'lodash'
import SingleSelectFilter from '../DataTable/FilterTool/Filters/SingleSelectFilter.tsx'
import MultiSelectFilter from '../DataTable/FilterTool/Filters/MultiSelectFilter.tsx'
import RangeFilter from '../DataTable/FilterTool/Filters/RangeFilter.tsx'
import TextFilter from '../DataTable/FilterTool/Filters/TextFilter.tsx'
import { useHasScrollbar, useMeasure } from '@vendorflow/common/lib/hooks'
import useScrollbarSize from 'react-scrollbar-size'
import Table from '../DataTable/TablePieces/Table.tsx'
import HeaderCell from '../DataTable/TablePieces/HeaderCell.tsx'
import TableBodyContent from '../DataTable/TablePieces/TableBodyContent.tsx'
import {
  EnhancedColumn,
  EnhancedHeader,
  EnhancedRow,
  EnhancedTableInstance,
  EnhancedTableOptions,
  EnhancedTableState,
  FilterType,
  TableFeatureOptions,
  UseDefaultTableInstanceProps,
} from '../../types'

export const ACTION_COLUMN_ID = 'action-column'
export const CELL_PADDING = 4
const EXPAND_BTN_WIDTH = 24
const NO_MATCH_TEXT = 'Sorry, no matching records found'

interface Props<D extends object> {
  title?: string
  'data-testid'?: string
  labels?: {
    noMatchLabel?: string
  }
  columns: Partial<EnhancedColumn<D>>[]
  instance: EnhancedTableInstance<D>
  options?: TableFeatureOptions<D>
  renderActions?: () => ReactNode
  renderRowSelectActions?: () => ReactNode
  ExpandedRow?: (props: { data: D }) => JSX.Element
  onContextMenu?: (evt: React.MouseEvent<HTMLDivElement, MouseEvent>, data: D) => void
}

export function DataTable<D extends object>({
  title,
  labels,
  columns,
  instance,
  options,
  renderActions,
  renderRowSelectActions,
  ExpandedRow,
  onContextMenu,
  ...restOfProps
}: Props<D>): JSX.Element {
  const tableHeadRef = useRef<HTMLDivElement>(null)
  const tableBodyRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement | null>(null)
  const { ref: tableBodyMeasureRefCallback, contentRect: tableBodyContentRect } = useMeasure()
  const { ref: rootRef, contentRect: rootContentRect } = useMeasure()
  const { ref: toolbarRef, contentRect: toolbarContentRect } = useMeasure()
  const { ref: paginationRef, contentRect: paginationContentRect } = useMeasure()
  const { ref: hasScrollbarRefCallback, hasScrollbar } = useHasScrollbar()
  const { width: scrollbarWidth } = useScrollbarSize()
  const [scrollLeft, setScrollLeft] = useState(0)
  const { noMatchLabel = NO_MATCH_TEXT } = labels || { noMatchLabel: NO_MATCH_TEXT }
  const {
    rowExpanding: rowExpandingConfig,
    filters: filtersConfig,
    globalSearch: globalSearchConfig,
    columnHiding: columnHidingConfig,
    pagination: paginationConfig,
    sorting: sortingConfig,
    table: tableConfig,
    virtualize: virtualizeConfig,
  } = options || {}
  const ExpandedComponent = ExpandedRow || rowExpandingConfig?.Component || null
  const {
    allColumns,
    getTableProps,
    gotoPage,
    headerGroups,
    page,
    prepareRow,
    rows,
    setAllFilters,
    setPageSize,
    setFilter,
    setGlobalFilter,
    state,
    totalColumnsWidth,
  } = instance
  const pageOptions = options?.pagination?.rowsPerPageOptions || instance.pageOptions
  const columnNameById = useMemo(() => {
    const result: SimpleMap<string> = {}

    function addNamesToMap(cols: Partial<EnhancedColumn<D>>[], result: SimpleMap<string>) {
      cols.forEach((col, index) => {
        const id =
          col.id != null ? (col.id as string) : typeof col.accessor === 'string' ? (col.accessor as string) : null

        if (!id) {
          throw new Error(
            `Please provide an id for column ${
              typeof col.Header === 'string' && !!col.Header ? col.Header : col.name ? col.name : `at index ${index}`
            }`
          )
        }

        if (col.filterName) {
          result[id] = col.filterName
        } else if (typeof col.Header === 'string') {
          result[id] = col.Header
        } else if (!col.name) {
          throw new Error(`Please provide a name for column with id = ${col.id}.`)
        } else {
          result[id] = col.name
        }

        if (Array.isArray(col?.columns)) {
          addNamesToMap(col.columns as EnhancedColumn<D>[], result)
        }
      })
    }

    addNamesToMap(columns, result)

    return result
  }, [columns])
  const { filters, globalFilter, pageIndex, pageSize, selectedRowIds } = state as EnhancedTableState<D>
  const { hasRowsSelected, numRowsSelected } = useMemo(() => {
    const hasRowsSelected = values != null ? values(selectedRowIds).some(Boolean) : false
    const numRowsSelected = values != null ? values(selectedRowIds).filter(Boolean).length : 0
    return { hasRowsSelected, numRowsSelected }
  }, [selectedRowIds])
  const tableRows = paginationConfig?.enabled ? page : rows
  const containerHeight =
    (rootContentRect.height || 0) - (toolbarContentRect.height || 0) - (paginationContentRect.height || 0)
  const defaultRowHeight = virtualizeConfig?.defaultRowHeight || 50
  const { getRowClassName } = options?.table || {}

  useEffect(() => {
    if (tableHeadRef.current) {
      tableHeadRef.current.scrollTo({ left: scrollLeft, behavior: 'auto' })
    }
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTo({ left: scrollLeft, behavior: 'auto' })
    }
  }, [scrollLeft])

  return (
    <Paper
      ref={rootRef}
      css={css`
        width: calc(100% - ${hasScrollbar.vertical ? scrollbarWidth : 0}px);
        height: 100%;
        position: relative;
      `}
      className="DataTable"
      {...restOfProps}
    >
      {!tableConfig?.hideToolbar && (
        <div ref={toolbarRef} className="DataTable__Toolbar">
          <Toolbar
            title={title}
            allColumns={allColumns as EnhancedHeader<D>[]}
            columnNameById={columnNameById}
            globalFilter={globalFilter}
            enableFilters={filtersConfig?.enabled}
            enableGlobalSearch={globalSearchConfig?.enabled}
            enableHideColumns={columnHidingConfig?.enabled}
            hasRowsSelected={hasRowsSelected}
            numRowsSelected={numRowsSelected}
            setAllFilters={setAllFilters}
            setGlobalFilter={
              globalSearchConfig?.setGlobalFilter ? globalSearchConfig?.setGlobalFilter : setGlobalFilter
            }
            renderActions={renderActions}
            renderRowSelectActions={renderRowSelectActions}
          />
          {filtersConfig?.enabled && (
            <AppliedFilters columnNameById={columnNameById} filters={filters} setFilter={setFilter} />
          )}
        </div>
      )}
      <div
        className="DataTable__TableContainer"
        css={css`
          height: ${containerHeight}px;
        `}
      >
        <Table
          getTableProps={getTableProps}
          css={css`
            width: 100%;
            display: grid;
            grid-template-rows: auto 1fr;
            grid-template-columns: calc(100% + ${hasScrollbar.vertical ? scrollbarWidth : 0}px);
          `}
        >
          <TableHead
            ref={tableHeadRef}
            className="DataTable__TableHead"
            component="div"
            css={css`
              && {
                display: block;
                height: auto;
                width: calc(100% - ${hasScrollbar.vertical ? scrollbarWidth : 0}px);
                overflow-x: auto;
                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */

                &::-webkit-scrollbar {
                  /* Hide scrollbar for Chrome, Safari and Opera */
                  display: none;
                }
              }
            `}
            onScroll={(evt: any) => {
              const { scrollLeft } = evt.target
              setScrollLeft(scrollLeft)
            }}
          >
            {headerGroups.map((headerGroup) => {
              return (
                <TableRow {...headerGroup.getHeaderGroupProps()} component="div">
                  {headerGroup.headers.map((header) => {
                    const { key, ...restOfHeaderProps } = header.getHeaderProps(
                      sortingConfig?.enabled ? (header as EnhancedHeader<D>).getSortByToggleProps() : undefined
                    )
                    return (
                      <HeaderCell
                        key={key}
                        header={header as EnhancedHeader<D>}
                        headerProps={restOfHeaderProps}
                        flexLayoutEnabled={tableConfig?.flexLayoutEnabled || false}
                      />
                    )
                  })}
                </TableRow>
              )
            })}
          </TableHead>
          <TableBodyContent
            defaultRowHeight={defaultRowHeight}
            ExpandedComponent={ExpandedComponent}
            flexLayoutEnabled={tableConfig?.flexLayoutEnabled || false}
            hasScrollbar={hasScrollbar}
            hasScrollbarRefCallback={hasScrollbarRefCallback}
            height={tableBodyContentRect.height}
            isVirtualizeEnabled={!!virtualizeConfig?.enabled}
            noMatchLabel={noMatchLabel}
            prepareRow={prepareRow}
            scrollContainerRef={scrollContainerRef}
            scrollbarWidth={scrollbarWidth}
            setScrollLeft={setScrollLeft}
            tableBodyRef={tableBodyRef}
            tableBodyMeasureRefCallback={tableBodyMeasureRefCallback}
            tableRows={tableRows}
            totalColumnsWidth={totalColumnsWidth}
            width={tableBodyContentRect.width}
            getRowClassName={getRowClassName}
            onContextMenu={onContextMenu}
          />
        </Table>
      </div>
      {paginationConfig?.enabled && (
        <TablePagination
          ref={paginationRef}
          className="DataTable__Pagination"
          component="div"
          count={rows.length}
          page={pageIndex}
          onPageChange={(_evt, newPage) => gotoPage(newPage)}
          onRowsPerPageChange={(evt) => setPageSize(Number(evt.target.value))}
          rowsPerPage={pageSize}
          rowsPerPageOptions={pageOptions}
        />
      )}
    </Paper>
  )
}

export function usePluginConfig<D extends object>(options: TableFeatureOptions<D>) {
  const {
    filters,
    globalSearch,
    pagination,
    rowExpanding,
    sorting,
    rowSelect,
    columnHiding,
    columnResizing,
    virtualize,
  } = options
  const actionColumnCellWidth =
    rowExpanding?.enabled && rowSelect?.enabled
      ? CELL_PADDING * 2 + EXPAND_BTN_WIDTH + CHECKBOX_WIDTH
      : Math.max(EXPAND_BTN_WIDTH, CHECKBOX_WIDTH) + CELL_PADDING * 2

  return useMemo(() => {
    const result: PluginHook<D>[] = []
    if (options.table?.flexLayoutEnabled) {
      result.push(useFlexLayout)
    } else {
      result.push(useBlockLayout)
    }

    if (filters?.enabled) {
      result.push(useFilters)
    }

    if (globalSearch?.enabled) {
      result.push(useGlobalFilter)
    }

    if (sorting?.enabled) {
      result.push(useSortBy)
    }

    if (rowExpanding?.enabled) {
      result.push(useExpanded)
    }

    if (pagination?.enabled) {
      result.push(usePagination)
    }

    if (rowSelect?.enabled) {
      result.push(useRowSelect)
    }

    if (columnResizing?.enabled) {
      result.push(useResizeColumns)
    }

    // column customizations
    if (rowSelect?.enabled || rowExpanding?.enabled) {
      result.push((hooks) => {
        hooks.visibleColumns.push((columns) => [
          {
            id: ACTION_COLUMN_ID,
            width: actionColumnCellWidth,
            minWidth: actionColumnCellWidth,
            maxWidth: actionColumnCellWidth,
            Header: (props) => {
              const { getToggleAllRowsSelectedProps, getToggleAllPageRowsSelectedProps } = props as PropsWithChildren<
                EnhancedTableInstance<D> & { column: ColumnInstance<D> }
              >
              return (
                <Fragment>
                  {rowExpanding?.enabled && (
                    <span
                      css={css`
                        padding: ${EXPAND_BTN_WIDTH / 2}px;
                      `}
                    />
                  )}
                  {rowSelect?.enabled && rowSelect?.mode === 'multi' ? (
                    // @ts-ignore
                    <IndeterminateCheckbox
                      {...(pagination?.enabled ? getToggleAllPageRowsSelectedProps() : getToggleAllRowsSelectedProps())}
                      // @ts-ignore
                      size="small"
                    />
                  ) : null}
                </Fragment>
              )
            },
            Cell: (props: CellProps<D>) => {
              const { getToggleRowSelectedProps, getToggleRowExpandedProps, isExpanded, index } =
                props.row as EnhancedRow<D>
              return (
                <Fragment>
                  {rowExpanding?.enabled && (
                    // @ts-ignore
                    <IconButton
                      {...getToggleRowExpandedProps()}
                      css={css`
                        &.MuiIconButton-root {
                          padding: 0;
                        }
                      `}
                      size="small"
                    >
                      <KeyboardArrowRight
                        css={css`
                          transform: rotate3d(0, 0, 1, ${isExpanded ? '90deg' : '0deg'});
                          transition: transform 0.25s;
                        `}
                      />
                    </IconButton>
                  )}
                  {rowSelect?.enabled && (
                    <IndeterminateCheckbox
                      name={index.toString()}
                      {...getToggleRowSelectedProps()}
                      // @ts-ignore
                      size="small"
                    />
                  )}
                </Fragment>
              )
            },
          },
          ...columns,
        ])
      })
    }

    return result
  }, [rowExpanding, rowSelect, columnHiding, sorting, filters, globalSearch, pagination, virtualize])
}

export function useDefaultTableInstance<D extends object>({
  columns,
  data,
  options = {},
  initialState = {},
  defaultColumn,
}: UseDefaultTableInstanceProps<D>) {
  const filterTypes: FilterTypes<D> = useMemo(
    () => ({
      equalsOneOf: (rows: Row<D>[], ids: string[], filterValue: any[]) => {
        return rows.filter((row) => {
          const rowValue = row.values[ids[0]]
          return !!filterValue && filterValue.length ? filterValue.includes(rowValue) : true
        })
      },
    }),
    []
  )

  function stateReducer<D extends object>(newState: TableState<D>, action: ActionType) {
    if (action.type === 'toggleRowExpanded') {
      if (options?.rowExpanding?.mode === 'single') {
        newState = produce(newState, (draft) => {
          // @ts-ignore
          ;(draft as EnhancedTableState<D>).expanded = {
            // since we are acting on the newState, we will just set this to its current value.
            // what's important is that no other expanded value will be set since this overwrites
            // the expanded object, so that it only contains this single entry.
            [action.id]: (draft as EnhancedTableState<D>).expanded[action.id],
          }
        })
      }
    }
    if (action.type === 'toggleRowSelected') {
      if (options?.rowSelect?.mode === 'single') {
        newState = produce(newState, (draft) => {
          // @ts-ignore
          ;(draft as EnhancedTableState<D>).selectedRowIds = {
            [action.id]: action.value,
          }
        })
      }
    }
    if (action.type === 'manuallySetColumnWidth') {
      const { columnId, width } = action as { type: string; columnId: string; width: number }
      newState = produce(newState, (draft) => {
        // @ts-ignore
        ;(draft as EnhancedTableState<D>).columnResizing.columnWidths[columnId] = width
      })
    }
    if (action.type === 'clearSelectedRows') {
      newState = produce(newState, (draft) => {
        ;(draft as EnhancedTableState<D>).selectedRowIds = {} as Record<IdType<D>, boolean>
      })
    }
    return newState
  }

  const plugins: PluginHook<D>[] = usePluginConfig<D>(options)
  const { enabled, pageSize, rowsPerPageOptions } = options?.pagination || {}
  let state = initialState
  if (enabled) {
    const initPageSize = pageSize
      ? pageSize
      : rowsPerPageOptions && rowsPerPageOptions.length
        ? rowsPerPageOptions[0]
        : undefined
    if (initPageSize) {
      state = produce(state, (draft) => {
        draft.pageSize = initPageSize
      })
    }
  }
  const { getRowId } = options?.table || {}
  const { autoResetExpanded = false } = options?.rowExpanding || {}
  const { autoResetGlobalFilter = false } = options?.globalSearch || {}
  const { autoResetSelectedRows = false } = options?.rowSelect || {}
  const { autoResetFilters = false } = options?.filters || {}
  const { autoResetSortBy = false } = options?.sorting || {}
  const { autoResetHiddenColumns = false } = options?.columnHiding || {}
  const { autoResetPage = false } = options?.pagination || {}
  const instance = useTable<D>(
    {
      autoResetExpanded,
      autoResetGlobalFilter,
      autoResetSelectedRows,
      autoResetFilters,
      autoResetSortBy,
      autoResetHiddenColumns,
      autoResetPage,
      getRowId,
      columns,
      data,
      defaultColumn,
      filterTypes,
      stateReducer,
      initialState: state,
    } as EnhancedTableOptions<D>,
    ...plugins
  ) as EnhancedTableInstance<D>

  function setColumnWidth(columnId: string, width: number) {
    instance.dispatch({ type: 'manuallySetColumnWidth', columnId, width })
  }

  function clearSelectedRows() {
    instance.dispatch({ type: 'clearSelectedRows' })
  }

  return {
    ...instance,
    setColumnWidth,
    clearSelectedRows,
  }
}

export function SingleLineCell({
  children,
  className,
  hasTooltip = true,
}: {
  children: ReactNode
  className?: string | undefined
  hasTooltip?: boolean
}) {
  const lineCellContent = (
    <div
      css={css`
        width: 100%;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      `}
      className={className}
    >
      {children}
    </div>
  )
  return hasTooltip ? <Tooltip title={children}>{lineCellContent}</Tooltip> : lineCellContent
}

export const getColumnFilterConfig = <D extends object>(
  filterType: FilterType
): { filter: string; Filter: Renderer<FilterProps<D>> } => {
  switch (filterType) {
    case 'select':
      // @ts-ignore
      return { filter: 'equals', Filter: SingleSelectFilter }
    case 'multi-select':
      // @ts-ignore
      return { filter: 'equalsOneOf', Filter: MultiSelectFilter }
    case 'range':
      // @ts-ignore
      return { filter: 'between', Filter: RangeFilter }
    case 'text':
    default:
      // @ts-ignore
      return { filter: 'text', Filter: TextFilter }
  }
}
